import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line } from 'recharts';

interface AccountData {
  annual_summary: any[];
  detailed_summary: any[];
}

interface AccountViewProps {
  data: AccountData;
  title: string;
}

const formatYAxis = (tickItem: number) => {
  if (tickItem >= 1000000) {
    return `${(tickItem / 1000000).toFixed(1)}M`;
  }
  if (tickItem >= 1000) {
    return `${(tickItem / 1000).toFixed(1)}K`;
  }
  return tickItem.toString();
};

const AccountView: React.FC<AccountViewProps> = ({ data, title }) => {
  const theme = useTheme();

  if (!data || !data.annual_summary || data.annual_summary.length === 0) {
    return (
      <Paper sx={{ p: 2, textAlign: 'center' }}>
        <Typography>No hay datos disponibles para {title}.</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Abonos Anuales - {title}</Typography>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data.annual_summary} margin={{ top: 20, right: 40, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Anio" />
            <YAxis yAxisId="left" orientation="left" stroke={theme.palette.text.secondary} tickFormatter={formatYAxis} />
            <YAxis yAxisId="right" orientation="right" stroke={theme.palette.secondary.main} tickFormatter={formatYAxis} />
            <Tooltip formatter={(value: number, name: string) => {
              return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(value);
            }} />
            <Legend verticalAlign="top" wrapperStyle={{ lineHeight: '40px' }} />
            <Bar yAxisId="left" dataKey="Total_Abonos" fill={theme.palette.primary.main} name="Total Abonos" />
            <Line yAxisId="right" type="monotone" dataKey="Total_Abonos" stroke={theme.palette.secondary.main} name="Tendencia" strokeDasharray="5 5" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6">Resumen Anual</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Año</TableCell>
                <TableCell>Banco</TableCell>
                <TableCell>Tipo Cuenta</TableCell>
                <TableCell align="right">Total Abonos</TableCell>
                <TableCell align="right">Análisis %</TableCell>
                <TableCell align="right">Análisis $</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.annual_summary.map((row: any) => (
                <TableRow key={row.Anio}>
                  <TableCell>{row.Anio}</TableCell>
                  <TableCell>{row.Banco}</TableCell>
                  <TableCell>{row.Tipo_Cuenta}</TableCell>
                  <TableCell align="right">{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(row.Total_Abonos)}</TableCell>
                  <TableCell align="right">{row['Analisis %'] ? row['Analisis %'].toFixed(2) : 'N/A'}%</TableCell>
                  <TableCell align="right">{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(row['Analisis $'])}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {data.detailed_summary && data.detailed_summary.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6">Detalle por Cuenta</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Año</TableCell>
                  <TableCell>Banco</TableCell>
                  <TableCell>Número Cuenta</TableCell>
                  <TableCell>Tipo Cuenta</TableCell>
                  <TableCell align="right">Total Abonos</TableCell>
                  <TableCell align="right">Total Cargos</TableCell>
                  <TableCell align="right">Total Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.detailed_summary.map((row: any, index: number) => (
                  <TableRow key={`${row.Numero_Cuenta}-${index}`}>
                    <TableCell>{row.Anio}</TableCell>
                    <TableCell>{row.Banco}</TableCell>
                    <TableCell>{row.Numero_Cuenta}</TableCell>
                    <TableCell>{row.Tipo_Cuenta}</TableCell>
                    <TableCell align="right">{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(row.Total_Abonos)}</TableCell>
                    <TableCell align="right">{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(row.Total_Cargos)}</TableCell>
                    <TableCell align="right">{new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(row.Total_Saldo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default AccountView;